import { Container } from '@cp/ds/src/components/container';
import React, { useMemo } from 'react';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { useClientWebResolver, useClientWebRouteParams } from '@cp/entities/clientWebRoutes';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { Box } from '@cp/ds/src/components/box';
import { useRouter } from '@cp/shared/router';
import { useCity } from '@cp/entities/location';
import { useClientProfile } from 'src/entities/client';
import { CITY_PATH_PARAMETER } from 'src/app/routes/city';
import { NotificationsSubscribeBanner } from 'src/features/notifications';
import { useIsExternalMode } from 'src/shared/ui/link/externalMode';
import { useHeaderFeatures, useSyncDimensions } from '../model';
import { HeaderProps } from './model';
import { HeaderContentMobile } from './content.mobile';
import { HeaderContentDesktop } from './content.desktop';

export * from './model';

export const Header: React.FC<HeaderProps> = ({
  onGoBack,
  rightBlock,
  leftBlock,
  sticky,
  trademarkSize,
  sx,
  containerProps,
  mobileFeatures,
  desktopFeatures,
}) => {
  const router = useRouter();
  const resolver = useClientWebResolver();
  const isExternalMode = useIsExternalMode();
  const { pageId } = useClientWebRouteParams();
  const containerRef = React.useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const clientProfile = useClientProfile();
  const { city } = useCity(clientProfile?.cityId);
  const features = useHeaderFeatures(mobileFeatures, desktopFeatures);

  const logoHref = React.useMemo(() => {
    return resolver.resolve(
      clientProfile?.mainCareerId && clientProfile.type
        ? {
            page: ClientWebPage.Catalog,
            pageProps: { careerId: clientProfile.mainCareerId, catalogType: clientTypeToSuitableCatalogItemType(clientProfile.type) },
            pathParams: { [CITY_PATH_PARAMETER]: city?.alias },
          }
        : { page: ClientWebPage.Home },
    );
  }, [city?.alias, clientProfile, resolver]);

  const handleLogoClick = () => {
    if (isExternalMode) {
      router.location.pathname = '/';
      return;
    }
    router.navigate(logoHref);
  };

  const HeaderContent = useMemo(() => (isMobile ? HeaderContentMobile : HeaderContentDesktop), [isMobile]);

  return (
    <Box
      component="header"
      ref={containerRef}
      sx={mergeSx(
        {
          zIndex: 5,
          ...((sticky ?? (clientProfile || !isMobile))
            ? {
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
              }
            : undefined),
        },

        sx,
      )}
    >
      <NotificationsSubscribeBanner />
      <Container
        {...containerProps}
        sx={mergeSx(
          { py: { xs: 1, md: 1.25, xl: 1.5 }, display: 'flex', alignItems: 'center', direction: 'row', justifyContent: 'space-between' },
          containerProps?.sx,
        )}
      >
        <HeaderContent
          clientProfile={clientProfile}
          containerRef={containerRef}
          features={features}
          leftBlock={leftBlock}
          logo={pageId !== ClientWebPage.CatalogItem}
          onGoBack={onGoBack}
          onLogoClick={handleLogoClick}
          rightBlock={rightBlock}
          trademarkSize={trademarkSize}
        />
      </Container>
    </Box>
  );
};

export default Header;
